// client/src/components/agendamentos/AgendamentoForm.js
import React, { useMemo, lazy, Suspense, useState, useEffect } from 'react';
import { Loader2, Home, MapPin, Calendar, Package, Building2, Truck } from 'lucide-react';

const ServicoAgendamentoForm = lazy(() => import('./ServicoAgendamentoForm'));
const RecursoAgendamentoForm = lazy(() => import('./RecursoAgendamentoForm'));

const FormLoader = () => (
    <div className="flex justify-center items-center py-12">
        <Loader2 size={32} className="animate-spin text-primary" />
        <span className="ml-2 text-gray-500">Carregando formulário...</span>
    </div>
);

// ✅ COMPONENTE DE SELEÇÃO DE FINALIDADE
const FinalidadeSelector = ({ onSelect, initialType = null, disabled = false }) => {
    const [selectedType, setSelectedType] = useState(initialType || null);

    useEffect(() => {
        if (initialType) {
            setSelectedType(initialType);
        }
    }, [initialType]);

    const handleSelect = (type) => {
        if (disabled) return;
        setSelectedType(type);
        onSelect(type);
    };

    const opcoes = [
        {
            id: 'servico_interno',
            titulo: 'Atendimento na Unidade',
            descricao: 'Serviços realizados dentro da clínica/unidade',
            icone: <Building2 size={24} />,
            cor: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
            corSelecionada: 'bg-blue-100 border-blue-500 ring-2 ring-blue-300',
            badge: 'Serviço Interno',
            tipo: 'servico',
            isExterno: false
        },
        {
            id: 'servico_externo',
            titulo: 'Atendimento Domiciliar',
            descricao: 'Profissional vai até o endereço do cliente',
            icone: <Home size={24} />,
            cor: 'bg-green-50 border-green-200 hover:bg-green-100',
            corSelecionada: 'bg-green-100 border-green-500 ring-2 ring-green-300',
            badge: 'Serviço Externo',
            tipo: 'servico',
            isExterno: true
        },
        {
            id: 'locacao_interna',
            titulo: 'Locação na Unidade',
            descricao: 'Aluguel de salas, equipamentos ou macas para uso dentro da unidade',
            icone: <Package size={24} />,
            cor: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
            corSelecionada: 'bg-purple-100 border-purple-500 ring-2 ring-purple-300',
            badge: 'Locação Interna',
            tipo: 'recurso',
            isExterno: false
        },
        {
            id: 'locacao_externa',
            titulo: 'Locação Externa',
            descricao: 'Aluguel de equipamentos ou materiais para uso fora da unidade',
            icone: <Truck size={24} />,
            cor: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
            corSelecionada: 'bg-orange-100 border-orange-500 ring-2 ring-orange-300',
            badge: 'Locação Externa',
            tipo: 'recurso',
            isExterno: true
        }
    ];

    return (
        <div className="finalidade-selector">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Qual o tipo de agendamento?</h3>
                <p className="text-sm text-gray-500 mt-1">
                    Selecione a finalidade para prosseguir com o agendamento
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opcoes.map(opcao => (
                    <button
                        key={opcao.id}
                        type="button"
                        onClick={() => handleSelect(opcao.id)}
                        disabled={disabled}
                        className={`
                            relative p-4 rounded-xl border-2 text-left transition-all duration-200
                            ${selectedType === opcao.id ? opcao.corSelecionada : opcao.cor}
                            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}
                        `}
                    >
                        <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 p-2 rounded-lg ${
                                selectedType === opcao.id ? 'bg-white' : 'bg-white/60'
                            }`}>
                                {React.cloneElement(opcao.icone, {
                                    className: selectedType === opcao.id ? 'text-indigo-600' : 'text-gray-500'
                                })}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <h4 className="font-semibold text-gray-800">{opcao.titulo}</h4>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/60 text-gray-600">
                                        {opcao.badge}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{opcao.descricao}</p>
                            </div>
                        </div>
                        {selectedType === opcao.id && (
                            <div className="absolute top-3 right-3">
                                <div className="w-4 h-4 bg-indigo-600 rounded-full"></div>
                            </div>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

const AgendamentoForm = ({ 
    onCloseForm, 
    current, 
    activeFilters, 
    clientes, 
    servicos, 
    recursos, 
    pacotesDoCliente,
    userServicos = [],
    unidades = [],
    agendaOptions = [],
    metodosPagamento = [],
    tipoForcado = null,
    onTipoChange = null
}) => {
    const [finalidadeSelecionada, setFinalidadeSelecionada] = useState(null);
    const [mostrarSeletor, setMostrarSeletor] = useState(true);

    const formConfig = useMemo(() => {
        if (tipoForcado) {
            if (tipoForcado === 'servico' || tipoForcado === 'servico_externo') {
                return { 
                    tipo: 'servico', 
                    isExterno: tipoForcado === 'servico_externo',
                    componente: ServicoAgendamentoForm
                };
            }
            if (tipoForcado === 'recurso' || tipoForcado === 'recurso_externo') {
                return { 
                    tipo: 'recurso', 
                    isExterno: tipoForcado === 'recurso_externo',
                    componente: RecursoAgendamentoForm
                };
            }
        }
        
        if (finalidadeSelecionada) {
            switch(finalidadeSelecionada) {
                case 'servico_interno':
                    return { tipo: 'servico', isExterno: false, componente: ServicoAgendamentoForm };
                case 'servico_externo':
                    return { tipo: 'servico', isExterno: true, componente: ServicoAgendamentoForm };
                case 'locacao_interna':
                    return { tipo: 'recurso', isExterno: false, componente: RecursoAgendamentoForm };
                case 'locacao_externa':
                    return { tipo: 'recurso', isExterno: true, componente: RecursoAgendamentoForm };
                default:
                    return null;
            }
        }
        
        if (current) {
            const subcategoria = current.tipoReserva?.subcategoria;
            if (subcategoria === 'INTERNO_SERVICOS') {
                return { tipo: 'servico', isExterno: false, componente: ServicoAgendamentoForm };
            }
            if (subcategoria === 'EXTERNO_SERVICOS') {
                return { tipo: 'servico', isExterno: true, componente: ServicoAgendamentoForm };
            }
            if (subcategoria === 'INTERNO_LOCACAO') {
                return { tipo: 'recurso', isExterno: false, componente: RecursoAgendamentoForm };
            }
            if (subcategoria === 'EXTERNO_LOCACAO') {
                return { tipo: 'recurso', isExterno: true, componente: RecursoAgendamentoForm };
            }
            
            if (current.itens && current.itens.length > 0) {
                const primeiroItem = current.itens[0];
                if (primeiroItem.modelo === 'Servico') {
                    const isExterno = current.tipoReserva?.isExterno || current.tipoServico === 'externo';
                    return { tipo: 'servico', isExterno, componente: ServicoAgendamentoForm };
                }
                if (primeiroItem.modelo === 'Recurso') {
                    const isExterno = current.tipoReserva?.isExterno || false;
                    return { tipo: 'recurso', isExterno, componente: RecursoAgendamentoForm };
                }
            }
        }
        
        if (activeFilters) {
            if (activeFilters.subcategoria) {
                if (activeFilters.subcategoria.includes('SERVICOS')) {
                    const isExterno = activeFilters.subcategoria.includes('EXTERNO');
                    return { tipo: 'servico', isExterno, componente: ServicoAgendamentoForm };
                }
                if (activeFilters.subcategoria.includes('LOCACAO')) {
                    const isExterno = activeFilters.subcategoria.includes('EXTERNO');
                    return { tipo: 'recurso', isExterno, componente: RecursoAgendamentoForm };
                }
            }
            
            if (activeFilters.tipoAgendamento) {
                if (activeFilters.tipoAgendamento === 'servico') {
                    return { tipo: 'servico', isExterno: false, componente: ServicoAgendamentoForm };
                }
                if (activeFilters.tipoAgendamento === 'servico_externo') {
                    return { tipo: 'servico', isExterno: true, componente: ServicoAgendamentoForm };
                }
                if (activeFilters.tipoAgendamento === 'recurso_interno') {
                    return { tipo: 'recurso', isExterno: false, componente: RecursoAgendamentoForm };
                }
                if (activeFilters.tipoAgendamento === 'recurso_externo') {
                    return { tipo: 'recurso', isExterno: true, componente: RecursoAgendamentoForm };
                }
            }
        }
        
        return null;
    }, [current, activeFilters, tipoForcado, finalidadeSelecionada]);

    useEffect(() => {
        if (onTipoChange && formConfig) {
            const tipoStr = formConfig.tipo === 'servico' 
                ? (formConfig.isExterno ? 'servico_externo' : 'servico')
                : (formConfig.isExterno ? 'recurso_externo' : 'recurso');
            onTipoChange(tipoStr);
        }
    }, [formConfig, onTipoChange]);

    useEffect(() => {
        if (tipoForcado) {
            setMostrarSeletor(false);
        } else if (current || (activeFilters?.subcategoria)) {
            setMostrarSeletor(false);
        } else if (!finalidadeSelecionada) {
            setMostrarSeletor(true);
        } else {
            setMostrarSeletor(false);
        }
    }, [tipoForcado, current, activeFilters, finalidadeSelecionada]);

    const handleFinalidadeSelect = (tipo) => {
        setFinalidadeSelecionada(tipo);
        setMostrarSeletor(false);
    };

    const handleBackToSelector = () => {
        setFinalidadeSelecionada(null);
        setMostrarSeletor(true);
        if (onTipoChange) onTipoChange(null);
    };

    const commonProps = {
        onCloseForm: onCloseForm || (() => {}),
        current: current || null,
        activeFilters: activeFilters || {},
        clientes: clientes || [],
        pacotesDoCliente: pacotesDoCliente || [],
        unidades: unidades || [],
        agendaOptions: agendaOptions || [],
        metodosPagamento: metodosPagamento || []
    };

    const servicoProps = {
        ...commonProps,
        servicos: servicos || [],
        userServicos: userServicos || []
    };

    const recursoProps = {
        ...commonProps,
        recursos: recursos || []
    };

    if (mostrarSeletor && !formConfig) {
        return (
            <div className="agendamento-form-container">
                <div className="finalidade-selector-wrapper">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-bold text-gray-800">Novo Agendamento</h2>
                        <button 
                            type="button" 
                            onClick={onCloseForm}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            ✕
                        </button>
                    </div>
                    <FinalidadeSelector 
                        onSelect={handleFinalidadeSelect}
                        initialType={finalidadeSelecionada}
                        disabled={false}
                    />
                </div>
            </div>
        );
    }

    if (formConfig) {
        const ComponenteForm = formConfig.componente;
        const props = formConfig.tipo === 'servico' ? servicoProps : recursoProps;
        
        return (
            <div className="agendamento-form-wrapper">
                {!current && !tipoForcado && (
                    <button
                        type="button"
                        onClick={handleBackToSelector}
                        className="back-to-selector-btn"
                    >
                        ← Voltar e escolher outra finalidade
                    </button>
                )}
                <Suspense fallback={<FormLoader />}>
                    <ComponenteForm 
                        key={`${formConfig.tipo}-${formConfig.isExterno}-form`} 
                        {...props}
                        tipoServicoInicial={formConfig.tipo === 'servico' ? (formConfig.isExterno ? 'externo' : 'interno') : undefined}
                        isLocacaoExterna={formConfig.tipo === 'recurso' && formConfig.isExterno}
                        onBackToSelector={!current && !tipoForcado ? handleBackToSelector : undefined}
                    />
                </Suspense>
                <style>{`
                    .back-to-selector-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.5rem;
                        margin: 1rem 0 0 1rem;
                        padding: 0.5rem 1rem;
                        font-size: 0.875rem;
                        font-weight: 500;
                        color: #4f46e5;
                        background: #eef2ff;
                        border: none;
                        border-radius: 0.5rem;
                        cursor: pointer;
                        transition: all 0.2s;
                    }
                    .back-to-selector-btn:hover {
                        background: #e0e7ff;
                        transform: translateX(-2px);
                    }
                `}</style>
            </div>
        );
    }

    return <FormLoader />;
};

export default AgendamentoForm;